<script setup lang="ts">
/*
 * Renders the toast stack from `stores/toast.ts` — bottom-right on desktop,
 * bottom-centre on phones. Mounted once in App.vue. Hover or focus holds a
 * toast's timer; the action is a link or a button (undo).
 */
import { Check, CircleAlert, Info, X } from 'lucide-vue-next';
import { RouterLink } from 'vue-router';
import { useToastStore, type Toast } from '../stores/toast';
import { useUiStore } from '../stores/ui';

const toast = useToastStore();
const ui = useUiStore();

const ICONS = { success: Check, error: CircleAlert, info: Info } as const;

const TONE_CLASS: Record<Toast['tone'], string> = {
  success: 'bg-mint text-text-inv',
  error: 'bg-error/20 text-error',
  info: 'bg-sky text-text-inv',
};

function act(t: Toast) {
  t.action?.onClick?.();
  toast.dismiss(t.id);
}
</script>

<!-- bottom-20 on phones clears the product page's fixed buy bar; while the
     400px mini cart is open the stack moves left of it so undo never hides Checkout. -->
<template>
  <div
    class="fixed inset-x-4 bottom-20 z-[80] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-5 sm:items-end pointer-events-none transition-[right] duration-200"
    :class="ui.miniCartOpen ? 'sm:right-[420px]' : 'sm:right-5'"
    aria-live="polite"
    aria-relevant="additions"
  >
    <TransitionGroup
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-3"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-for="item in toast.items"
        :key="item.id"
        class="surface animate-pop pointer-events-auto flex w-full max-w-[380px] items-center gap-3 p-3 pr-2 shadow-lift"
        role="status"
        @mouseenter="toast.hold(item.id)"
        @mouseleave="toast.release(item.id)"
        @focusin="toast.hold(item.id)"
        @focusout="toast.release(item.id)"
      >
        <img v-if="item.image" :src="item.image" alt="" class="h-12 w-9 shrink-0 rounded-sm object-cover bg-bg-mute" />
        <span v-else class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" :class="TONE_CLASS[item.tone]">
          <component :is="ICONS[item.tone]" class="h-4 w-4" aria-hidden="true" />
        </span>

        <div class="min-w-0 flex-1">
          <p class="text-[14px] font-semibold leading-tight text-text-base">{{ item.title }}</p>
          <p v-if="item.body" class="mt-0.5 truncate text-[12px] text-text-dim">{{ item.body }}</p>
        </div>

        <RouterLink
          v-if="item.action?.to"
          :to="item.action.to"
          class="btn-ghost btn-sm shrink-0 font-heading text-accent hover:text-accent-2"
          @click="toast.dismiss(item.id)"
        >
          {{ item.action.label }}
        </RouterLink>
        <button
          v-else-if="item.action"
          type="button"
          class="btn-ghost btn-sm shrink-0 font-heading text-accent hover:text-accent-2"
          @click="act(item)"
        >
          {{ item.action.label }}
        </button>

        <button
          type="button"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-text-faint hover:bg-bg-mute hover:text-text-base"
          :aria-label="$t('common.actions.close')"
          @click="toast.dismiss(item.id)"
        >
          <X class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>
